/**
 * Cart Item Component
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useDebouncedCallback } from '@/lib/hooks/use-debounced-callback';
import { CART_QUANTITY_DEBOUNCE_MS } from '@/lib/cart/constants';
import { formatPrice, IconButton, Text, OptimizedImage } from '@template-project/afrodita-components';
import { ShoppingBag, Trash2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useIntl } from '@/lib/intl';
import { CartItemEntity } from '@/domain/entities/cart.entity';
import {
  CART_ITEM_ACCESSIBILITY_DECREASE,
  CART_ITEM_ACCESSIBILITY_INCREASE,
  CART_ITEM_REMOVE,
} from './i18.keys';
import { resolveCdnHosts } from '@/lib/image/cdn-hosts';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};

  &[data-loading='true'] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const StyledImageContainer = styled.a`
  flex-shrink: 0;
  width: 82px;
  overflow: hidden;

  img {
    border-radius: ${({ theme }) => theme.radius.small};
    width: 82px;
    height: 82px;
    object-fit: cover;
  }
`;

const ImagePlaceholder = styled.div`
  width: 82px;
  height: 82px;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.shades[200].color};
`;

const DetailsWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const FirstRow = styled.div`
  display: flex;
  gap: 0;
  flex: 1;
  justify-content: space-between;
`;

const SecondRow = styled.div`
  display: flex;
  align-items: center;
  align-content: flex-end;
  gap: ${({ theme }) => theme.space.sm};
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.space.sm};
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.space.xs};
  flex: 1;
`;

const Title = styled(Text)`
  display: block;
  max-width: 200px;
`;

const RemoveItemButton = styled(IconButton)`
  margin: 0;
  justify-content: flex-end;
  height: fit-content;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
    opacity: 1;
  }
`;

const Pricing = styled.div`
  display: flex;
  align-items: flex-end;
  margin-left: auto;
  flex-direction: column;
  gap: 2px;
`;

const OriginalPrice = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration-line: line-through;
`;

const FinalPrice = styled(Text)``;

const QtyPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const QtyStepperButton = styled(IconButton)`
  width: ${({ theme }) => theme.space['2xl']};
  height: ${({ theme }) => theme.space['2xl']};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.secondary};
    opacity: 1;
  }
`;

const QtyDisplay = styled.span`
  min-width: ${({ theme }) => theme.space['2xl']};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
`;

export interface CartItemProps {
  item: CartItemEntity;
  /** May return a promise; optimistic qty stays until cart line qty catches up. */
  onQuantityChange: (quantity: number) => void | Promise<void>;
  onRemove: () => void;
  isLoading?: boolean;
}

export function CartItem({ item, onQuantityChange, onRemove, isLoading = false }: CartItemProps) {
  const { t } = useTranslation('sidecart');
  const { countryConfig, localizedPath } = useIntl();
  const [quantity, setQuantity] = useState(item.quantity);
  const pendingQuantityRef = useRef(item.quantity);
  const hasPendingPersistRef = useRef(false);
  /** Cart line qty when the current optimistic edit started (for server override detection). */
  const quantityAtPendingStartRef = useRef(item.quantity);
  const persistInFlightRef = useRef(false);
  const itemQuantityRef = useRef(item.quantity);
  itemQuantityRef.current = item.quantity;

  const debouncedPersistQuantity = useDebouncedCallback(() => {
    const targetQuantity = pendingQuantityRef.current;
    persistInFlightRef.current = true;
    void Promise.resolve(onQuantityChange(targetQuantity)).finally(() => {
      persistInFlightRef.current = false;
    });
  }, CART_QUANTITY_DEBOUNCE_MS);

  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  useEffect(() => {
    if (hasPendingPersistRef.current) {
      if (item.quantity === pendingQuantityRef.current) {
        hasPendingPersistRef.current = false;
        return;
      }

      const persistSettled =
        !debouncedPersistQuantity.isScheduled() && !persistInFlightRef.current;
      const serverAdjustedQuantity =
        persistSettled && item.quantity !== quantityAtPendingStartRef.current;

      if (serverAdjustedQuantity) {
        hasPendingPersistRef.current = false;
        setQuantity(item.quantity);
        pendingQuantityRef.current = item.quantity;
      }

      return;
    }

    setQuantity(item.quantity);
    pendingQuantityRef.current = item.quantity;
  }, [item.quantity, debouncedPersistQuantity]);

  useEffect(
    () => () => {
      debouncedPersistQuantity.flush();
    },
    [debouncedPersistQuantity],
  );

  const applyQuantity = (newQuantity: number) => {
    if (newQuantity < 1) {
      hasPendingPersistRef.current = false;
      onRemove();
      return;
    }
    if (!hasPendingPersistRef.current) {
      quantityAtPendingStartRef.current = itemQuantityRef.current;
    }
    pendingQuantityRef.current = newQuantity;
    setQuantity(newQuantity);
    hasPendingPersistRef.current = true;
    debouncedPersistQuantity();
  };

  const handleIncrement = () => {
    applyQuantity(pendingQuantityRef.current + 1);
  };

  const handleDecrement = () => {
    if (pendingQuantityRef.current <= 1) {
      return;
    }
    applyQuantity(pendingQuantityRef.current - 1);
  };

  // formatPrice expects cents, not dollars, so use the raw cent values
  const price = item.price.display;
  const comparePrice = item.compare_at_price?.display || 0;
  const hasDiscount = comparePrice > price;

  return (
    <Wrapper data-loading={isLoading}>
      <StyledImageContainer href={localizedPath(`/products/${item.product_id}`)}>
        {item.image_url ? (
          <OptimizedImage
            src={item.image_url}
            alt={item.name}
            sizes="80px"
            widths={[80, 160]}
            width={82}
            height={82}
            cdnHosts={resolveCdnHosts()}
          />
        ) : (
          <ImagePlaceholder>
            <ShoppingBag size={32} aria-hidden />
          </ImagePlaceholder>
        )}
      </StyledImageContainer>

      <DetailsWrapper>
        <FirstRow>
          <TextContent>
            <Title variant="bodySmall" weight="semibold" as="span" data-testid="line-title">
              {item.name}
            </Title>
          </TextContent>

          <RemoveItemButton
            data-testid="CartProductItemCloseBtn"
            onClick={onRemove}
            aria-label={t(CART_ITEM_REMOVE)}
            title={t(CART_ITEM_REMOVE)}
            variant="destructive"
            size="sm"
            icon={<Trash2 size={18} aria-hidden />}
          />
        </FirstRow>

        <SecondRow>
          <QtyPickerWrapper>
            <QtyStepperButton
              onClick={handleDecrement}
              disabled={quantity <= 1}
              aria-label={t(CART_ITEM_ACCESSIBILITY_DECREASE)}
              variant="outline"
              size="sm"
              icon={<span>−</span>}
            />
            <QtyDisplay data-testid="quantity-display">{quantity}</QtyDisplay>
            <QtyStepperButton
              onClick={handleIncrement}
              aria-label={t(CART_ITEM_ACCESSIBILITY_INCREASE)}
              variant="outline"
              size="sm"
              icon={<span>+</span>}
            />
          </QtyPickerWrapper>

          <Pricing>
            {hasDiscount && (
              <OriginalPrice
                variant="bodySmall"
                as="span"
                data-testid="original-product-price"
              >
                {formatPrice(comparePrice, priceOptions)}
              </OriginalPrice>
            )}
            <FinalPrice
              variant="heading6"
              weight="bold"
              as="span"
              data-testid="final-product-price"
            >
              {formatPrice(price, priceOptions)}
            </FinalPrice>
          </Pricing>
        </SecondRow>
      </DetailsWrapper>
    </Wrapper>
  );
}
